import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
} from "date-fns";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import * as S from "../styles";

interface MiniCalendarProps {
  currentDate: Date;
  onSelectDate: (date: Date) => void;
  onClose: () => void;
}

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

const MiniCalendar: React.FC<MiniCalendarProps> = ({
  currentDate,
  onSelectDate,
  onClose,
}) => {
  const [viewMonth, setViewMonth] = useState(currentDate);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setViewMonth(currentDate);
  }, [currentDate]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [viewMonth]);

  const title = useMemo(() => {
    const label = viewMonth.toLocaleDateString("pt-BR", {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [viewMonth]);

  const today = new Date();

  return (
    <S.MiniCalendarContainer ref={containerRef}>
      <S.MiniCalendarHeader>
        <S.MiniCalendarNavButton
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
        >
          <MdChevronLeft />
        </S.MiniCalendarNavButton>
        <S.MiniCalendarTitle>{title}</S.MiniCalendarTitle>
        <S.MiniCalendarNavButton
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
        >
          <MdChevronRight />
        </S.MiniCalendarNavButton>
      </S.MiniCalendarHeader>

      <S.MiniCalendarGrid>
        {WEEKDAYS.map((wd, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <S.MiniCalendarWeekday key={i}>{wd}</S.MiniCalendarWeekday>
        ))}

        {calendarDays.map((day) => (
          <S.MiniCalendarDay
            key={day.toISOString()}
            $isToday={isSameDay(day, today)}
            $isSelected={isSameDay(day, currentDate)}
            $isCurrentMonth={isSameMonth(day, viewMonth)}
            onClick={() => onSelectDate(day)}
          >
            {day.getDate()}
          </S.MiniCalendarDay>
        ))}
      </S.MiniCalendarGrid>

      <S.MiniCalendarTodayButton onClick={() => onSelectDate(new Date())}>
        Hoje
      </S.MiniCalendarTodayButton>
    </S.MiniCalendarContainer>
  );
};

export default MiniCalendar;
